import { Grid2, Typography, Box, Backdrop } from "@mui/material";
import { Styled } from "../styles";
import { Button, Input } from "@aidergroup/ui-v2";
import { i18n } from "@/utils/i18n";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import schemas from "@/utils/schemas";
import React from "react";
import { Forms } from "@/types/form";
import useUser from "@/context/user";
import constant from "@/utils/constant";
import { useLinkGoogle, useSubmit, useUnlinkGoogle } from "./hooks";
import GoogleIcon from "@mui/icons-material/Google";
import GoogleButton from "@/components/ui/google-button";

const PasswordForm: React.FC = () => {
    const user = useUser(state => state);
    const inputSpacing = constant.GRID_INPUT_SPACING;
    const sectionSpacing = constant.GRID_SECTION_SPACING;

    const providers = user.providerData.map(i => i.providerId);
    const hasOnlyGoogleProvider = providers.length === 1 && providers[0] === "google.com";
    const hasOnlyEmailProvider = providers.length === 1 && providers[0] === "password";

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, isDirty },
        reset,
    } = useForm<Forms["ChangePassword"]>({
        resolver: yupResolver(schemas.changePassword),
        values: {
            currentPassword: "",
            newPassword: "",
            repeatNewPassword: "",
            password: "",
            repeatPassword: "",
        },
    });

    const onSubmit = useSubmit(reset);
    const { unLinking, onUnlink } = useUnlinkGoogle();
    const { linking, onLink } = useLinkGoogle();
    const isLinking = unLinking || linking;

    return (
        <Box component="form" onSubmit={handleSubmit(async data => await onSubmit(data))}>
            <Styled.Section
                container
                direction="column"
                alignItems="center"
                spacing={sectionSpacing}
            >
                {hasOnlyGoogleProvider ? (
                    <Grid2 container direction="column">
                        <Grid2>
                            <Box display="flex" alignItems="center">
                                <GoogleIcon style={{ marginRight: "10px" }} />
                                <Typography>
                                    You've used Google provider to sign in. You can create a new
                                    password to use a password sign-in in combination with google
                                    sign-in.
                                </Typography>
                            </Box>
                        </Grid2>
                        <Grid2 size={4}>
                            <Input
                                control={control}
                                name="password"
                                label={i18n.t("label.password")}
                                placeholder={i18n.t("placeholder.password")}
                                type="password"
                                fullWidth
                            />
                        </Grid2>
                        <Grid2 size={4}>
                            <Input
                                control={control}
                                name="repeatPassword"
                                label={i18n.t("label.repeatPassword")}
                                placeholder={i18n.t("placeholder.repeatPassword")}
                                type="password"
                                fullWidth
                            />
                        </Grid2>
                    </Grid2>
                ) : (
                    <Grid2 container direction="column">
                        <Grid2 container spacing={inputSpacing}>
                            <Grid2 size={4}>
                                <Input
                                    control={control}
                                    name="currentPassword"
                                    label={i18n.t("label.currentPassword")}
                                    placeholder={i18n.t("placeholder.currentPassword")}
                                    type="password"
                                    fullWidth
                                />
                            </Grid2>
                            <Grid2 size={4}>
                                <Input
                                    control={control}
                                    name="newPassword"
                                    label={i18n.t("label.newPassword")}
                                    placeholder={i18n.t("placeholder.newPassword")}
                                    type="password"
                                    fullWidth
                                />
                            </Grid2>
                            <Grid2 size={4}>
                                <Input
                                    control={control}
                                    name="repeatNewPassword"
                                    label={i18n.t("label.repeatNewPassword")}
                                    placeholder={i18n.t("placeholder.repeatNewPassword")}
                                    type="password"
                                    fullWidth
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                )}
                <Grid2>
                    <Button
                        variant="contained"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isDirty}
                    >
                        {i18n.t("button.changePassword")}
                    </Button>
                </Grid2>
            </Styled.Section>
            {hasOnlyGoogleProvider ? null : (
                <Grid2 alignSelf="flex-start">
                    <GoogleButton
                        data-testid="profile-google-button"
                        text={i18n.t(hasOnlyEmailProvider ? "linkGoogle" : "unlinkGoogle", {
                            scope: "button",
                        })}
                        onClick={hasOnlyEmailProvider ? onLink : onUnlink}
                    />
                </Grid2>
            )}
            <Backdrop open={isLinking} />
        </Box>
    );
};

export default PasswordForm;
